import { LoadingArea } from '@consigli/facade';
import {
  useProjectId,
  usePackageId,
  usePackage,
  useFindAddressByCoordinates,
} from '@consigli/hooks';
import { FC, lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';

const MapView = lazy(() => import('./map-view'));

const MAP_HEIGHT = 'h-[calc(95vh-280px)]';
const MAP_WIDTH = 'w-full';

const ReadOnlyMapView: FC<{
  lat: number | null;
  lon: number | null;
  address?: string;
}> = ({ lat, lon, address }) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return <div className={`${MAP_WIDTH} ${MAP_HEIGHT} bg-gray-100 rounded-lg animate-pulse`} />;
  }

  return (
    <Suspense
      fallback={
        <div className={`${MAP_WIDTH} ${MAP_HEIGHT} bg-gray-100 rounded-lg animate-pulse`} />
      }
    >
      <MapView
        lat={lat ?? undefined}
        lon={lon ?? undefined}
        address={address}
        showMarker={!!(lat && lon)}
        zoom={lat && lon ? 15 : 10}
        center={lat && lon ? [lat, lon] : undefined}
        autoCenter={true}
        hideSearch={true}
        hideCoordinates={true}
        height={MAP_HEIGHT}
        width={MAP_WIDTH}
      />
    </Suspense>
  );
};

export const LocationPage: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();

  // Fetch the document package to get the data field
  const { pkg, isLoading } = usePackage(projectId, packageId);

  // Get the location coordinates from the package's data field
  const { lat, lon } = useMemo(() => {
    const data = pkg?.data as Record<string, unknown> | undefined;

    if (!data) {
      return { lat: null, lon: null };
    }

    const locationLat = data.locationLat;
    const locationLon = data.locationLon;

    return {
      lat: locationLat != null ? Number(locationLat) : null,
      lon: locationLon != null ? Number(locationLon) : null,
    };
  }, [pkg?.data]);

  // Reverse geocode to get the address
  const { address, isLoading: isLoadingAddress } = useFindAddressByCoordinates(lat, lon);

  const hasValidCoordinates = lat !== null && lon !== null && !isNaN(lat) && !isNaN(lon);

  return (
    <WorkspaceProvider>
      <WorkspaceLayout>
        <Breadcrumb label={t('servicetabs.location', 'Location')} />
        {isLoading ? (
          <LoadingArea title={t('location.loading', 'Loading location...')} />
        ) : !hasValidCoordinates ? (
          <div className="flex justify-center items-center w-full h-64 text-day-neutral-subtle text-lg">
            {t('location.no-coordinates', 'No location coordinates available')}
          </div>
        ) : (
          <div className="p-4">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-day-neutral-dark mb-2">
                {t('location.site-location', 'Site Location')}
              </h2>
              {isLoadingAddress ? (
                <p className="text-sm text-day-neutral-subtle">
                  {t('location.loading-address', 'Loading address...')}
                </p>
              ) : address ? (
                <p className="text-sm text-day-neutral-dark mb-1">{address}</p>
              ) : null}
              <p className="text-sm text-day-neutral-subtle">
                {t('location.coordinates-info', 'Coordinates')}: {lat?.toFixed(6)},{' '}
                {lon?.toFixed(6)}
              </p>
            </div>
            <ReadOnlyMapView lat={lat} lon={lon} address={address ?? undefined} />
          </div>
        )}
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
